import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as CheckCircleIcon } from '@/assets/icons/check-circle.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import { ReactComponent as XCircleIcon } from '@/assets/icons/x-circle.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerHolidayRequestResource } from '@/types/resources';
import { api } from '@/utils/api';

import VacationStatusDot from '../VacationStatusDot';
import VacationTypeBadge from '../VacationTypeBadge';
import FreelancerVacationRequestModal from './FreelancerVacationRequestModal';

const GrayTd = styled(Td)`
  color: ${({ theme }) => theme.colors.gray};
  padding: 20px;
`;

interface FreelancersVacationTableRowProps {
  request: FreelancerHolidayRequestResource;
}

const FreelancersVacationTableRow = ({
  request,
}: FreelancersVacationTableRowProps) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const alerts = useAlerts();

  const userCanManageData = useHasPermission('manage freelancers');

  const queryClient = useQueryClient();
  const vacationPeriod = `${request.startDate ? DateTime.fromISO(request.startDate).toFormat('yyyy/LL/dd') : '-'} - ${request.endDate ? DateTime.fromISO(request.endDate).toFormat('yyyy/LL/dd') : '-'}`;
  const deductedDaysText = `${request.totalDays} ${request.totalDays === 1 ? 'day' : 'days'}`;

  const handleApprove = async () => {
    const confirmed = await alerts.confirm('Are you sure you want to confirm?');
    if (!confirmed) {
      return;
    }
    try {
      await api.post(
        `/v1/payments/freelancers/holidays/requests/${request.id}/approve`,
        { approve_type: userCanManageData ? 'hr' : 'team_lead' },
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
        exact: false,
      });
      alerts.success('Vacation request approved successfully');
    } catch (e) {
      alerts.error('Failed to approve vacation request');
    }
  };

  const handleDecline = async () => {
    const confirmed = await alerts.confirm('Are you sure you want to decline?');
    if (!confirmed) {
      return;
    }
    try {
      await api.post(
        `/v1/payments/freelancers/holidays/requests/${request.id}/reject`,
        { decline_type: userCanManageData ? 'hr' : 'team_lead' },
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
        exact: false,
      });
      alerts.success('Vacation request declined successfully');
    } catch (e) {
      alerts.error('Failed to decline vacation request');
    }
  };

  const handleDelete = async () => {
    const confirmed = await alerts.confirm(
      'Are you sure you want to delete vacation request?',
    );
    if (!confirmed) {
      return;
    }
    try {
      await api.delete(
        `/v1/payments/freelancers/holidays/requests/${request.id}`,
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
        exact: false,
      });
      alerts.success('Vacation request deleted successfully');
    } catch (e) {
      alerts.error('Failed to delete vacation request');
    }
  };

  return (
    <>
      <tr>
        <GrayTd>
          {DateTime.fromISO(request.requestedAt).toFormat('yyyy/LL/dd') || '-'}
        </GrayTd>
        <Td>{vacationPeriod}</Td>
        <Td>{deductedDaysText}</Td>
        <Td>
          <VacationTypeBadge type={request.type} />
        </Td>
        <Td>
          <VacationStatusDot status={request.teamLeadStatus} />
        </Td>
        <Td>
          <VacationStatusDot status={request.hrStatus} />
        </Td>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton as="button" onClick={handleApprove}>
                <CheckCircleIcon />
                Approve
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={handleDecline}>
                <XCircleIcon />
                Decline
              </ActionsDropdownButton>
              {userCanManageData && (
                <>
                  <ActionsDropdownButton
                    as="button"
                    onClick={() => setShowEditModal(true)}
                  >
                    <PencilIcon />
                    Edit
                  </ActionsDropdownButton>
                  <ActionsDropdownButton as="button" onClick={handleDelete}>
                    <TrashIcon />
                    Delete
                  </ActionsDropdownButton>
                </>
              )}
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
      <AnimatePresence>
        {showEditModal && (
          <FreelancerVacationRequestModal
            request={request}
            onClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancersVacationTableRow;
